"use client";

import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, PlusIcon } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";

import PricingDialog from "@/components/dashboard/PricingDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useValidateEntitlement } from "@/hooks/use-usage";

export interface TreeItem {
  id?: string;
  name: string;
  children?: TreeItem[];
}

function areEqual(prev: TreeDropdownProps, next: TreeDropdownProps) {
  return (
    prev.data === next.data &&
    prev.onSelect === next.onSelect &&
    prev.selectedItemName === next.selectedItemName &&
    prev.loading === next.loading &&
    prev.dependencyKey === next.dependencyKey &&
    prev.showCreateTeam === next.showCreateTeam &&
    prev.side === next.side &&
    prev.align === next.align &&
    prev.sideOffset === next.sideOffset &&
    prev.currentPlanName === next.currentPlanName
    // Intentionally ignore trigger/renderTrigger identity to prevent rerenders from inline elements
  );
}

export const TreeDropdown = memo(TreeDropdownInner, areEqual);

export interface TreeDropdownProps {
  data: TreeItem[];
  onSelect?: (item: TreeItem) => void;
  selectedItemName?: string | null;
  loading?: boolean;
  // Optional: a changing value (e.g., query dataUpdatedAt, orgId) to force recalculation
  dependencyKey?: unknown;
  // Control visibility of the Create team button
  showCreateTeam?: boolean;
  // Optional custom trigger (e.g., icon button in collapsed sidebar)
  trigger?: React.ReactNode;
  // Optional trigger renderer with access to open state for chevron rotation, etc.
  renderTrigger?: (opts: { isOpen: boolean }) => React.ReactNode;
  // Control menu positioning (useful to open to the right in collapsed state)
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  // Optional current plan name for display in the dropdown footer
  currentPlanName?: string | null;
}

const TreeItem: React.FC<{
  item: TreeItem;
  level: number;
  onSelect?: (item: TreeItem) => void;
  onItemSelect?: () => void;
  selectedItemName?: string | null;
  currentPlanName?: string | null;
}> = ({
  item,
  level,
  onSelect,
  onItemSelect,
  selectedItemName,
  currentPlanName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const childrenRef = useRef<HTMLDivElement>(null);
  const hasChildren = item.children && item.children.length > 0;

  useEffect(() => {
    if (isOpen && childrenRef.current) {
      // Wait for next tick to ensure children are rendered
      setTimeout(() => {
        childrenRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 0);
    }
  }, [isOpen]);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      setIsOpen(!isOpen);
    }
  };

  const handleItemClick = () => {
    if (onSelect) {
      onSelect(item);
      onItemSelect?.();
    }
  };

  return (
    <div className="w-full select-none">
      <div
        className="flex cursor-pointer items-center rounded-sm px-2 py-1 hover:bg-accent"
        style={{ paddingLeft: `${level * 12}px` }}
        onClick={handleItemClick}
      >
        <span className="mr-2" onClick={handleExpandClick}>
          {hasChildren ? (
            isOpen ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )
          ) : (
            <div className="flex size-5 items-center justify-center rounded-md bg-sky-600 text-[11px] font-semibold text-white">
              {(selectedItemName?.[0] || "T").toUpperCase()}
            </div>
          )}
        </span>
        <span className="flex flex-1 items-center justify-between gap-2">
          <span>{item.name}</span>
          {selectedItemName &&
            currentPlanName &&
            item.name === selectedItemName && (
              <span className="shrink-0 rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-medium text-white">
                {currentPlanName}
              </span>
            )}
        </span>
      </div>
      {isOpen && hasChildren && item.children && (
        <div ref={childrenRef}>
          {item.children.map((child, index) => (
            <TreeItem
              key={child.name + index}
              item={child}
              level={level + 1}
              onSelect={onSelect}
              onItemSelect={onItemSelect}
              selectedItemName={selectedItemName}
              currentPlanName={currentPlanName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

TreeItem.displayName = "TreeItem";

function TreeDropdownInner({
  data,
  onSelect,
  selectedItemName,
  loading = false,
  dependencyKey,
  showCreateTeam = true,
  trigger,
  renderTrigger,
  side,
  align,
  sideOffset,
  currentPlanName,
}: TreeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const params = useParams();
  const orgId = (params as { orgId?: string })?.orgId;

  const { data: teamsEntitlement } = useValidateEntitlement(orgId, "Teams");

  const canCreateTeam =
    !!teamsEntitlement &&
    !!teamsEntitlement.isAvailable &&
    (teamsEntitlement.remaining ?? 0) > 0;

  // Close menu when an explicit dependencyKey changes; avoid reacting to data identity by default
  useEffect(() => {
    if (dependencyKey === undefined) return;
    setIsOpen(false);
  }, [dependencyKey]);

  // Create a more efficient memoized version of the dropdown content
  const DropdownContent = useMemo(() => {
    const Content = () => (
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className="z-50 max-h-[300px] min-w-[250px] overflow-y-auto rounded-lg border bg-popover p-0 shadow-md"
      >
        <div className="p-2">
          {data.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground">
              No items found
            </div>
          ) : (
            data.map((item, index) => (
              <TreeItem
                key={item.id || `${item.name}-${index}`}
                item={item}
                level={0}
                onSelect={onSelect}
                onItemSelect={() => setIsOpen(false)}
                selectedItemName={selectedItemName}
                currentPlanName={currentPlanName}
              />
            ))
          )}
          {showCreateTeam && (
            <div className="mt-2 flex items-center justify-start">
              {canCreateTeam ? (
                <Button
                  variant="ghost"
                  className="w-auto justify-start px-2 py-1 text-[11px]"
                  asChild
                >
                  <Link href="/auth/onboarding">
                    <PlusIcon className="mr-1 size-3" /> Create team
                  </Link>
                </Button>
              ) : (
                <PricingDialog
                  trigger={
                    <Button
                      variant="ghost"
                      className="w-auto justify-start px-2 py-1 text-[11px]"
                    >
                      <PlusIcon className="mr-1 size-3" /> Create team
                    </Button>
                  }
                />
              )}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    );

    Content.displayName = "DropdownContent";
    return Content;
  }, [
    data,
    onSelect,
    setIsOpen,
    side,
    align,
    sideOffset,
    dependencyKey,
    showCreateTeam,
    canCreateTeam,
    currentPlanName,
  ]);

  // Calculate the max width based on the longest item name
  const maxNameLength = useMemo(() => {
    let maxLength = selectedItemName?.length || 0;

    // Find the longest name in the data
    const findLongestName = (items: TreeItem[]) => {
      items.forEach((item) => {
        if (item.name && item.name.length > maxLength) {
          maxLength = item.name.length;
        }
        if (item.children && item.children.length > 0) {
          findLongestName(item.children);
        }
      });
    };

    findLongestName(data);

    // Set a minimum and maximum width (in characters)
    return Math.max(15, Math.min(40, maxLength));
  }, [data, selectedItemName, dependencyKey]);

  // Create a more efficient memoized version of the button content
  const ButtonContent = useMemo(() => {
    const Content = () => (
      <div className="flex w-full items-center gap-2">
        {loading ? (
          <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <div className="flex size-5 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
            {(selectedItemName?.[0] || "T").toUpperCase()}
          </div>
        )}
        <span
          className="truncate"
          style={{ maxWidth: `${maxNameLength * 0.6}rem` }}
        >
          {selectedItemName || "Select Organization"}
        </span>
        {isOpen ? (
          <ChevronDown className="ml-auto size-4 rotate-180 opacity-70 transition-transform" />
        ) : (
          <ChevronDown className="ml-auto size-4 opacity-70 transition-transform" />
        )}
      </div>
    );

    Content.displayName = "ButtonContent";
    return Content;
  }, [loading, selectedItemName, maxNameLength, isOpen, dependencyKey]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {renderTrigger ? (
          <div>{renderTrigger({ isOpen })}</div>
        ) : trigger ? (
          // Use custom trigger when provided (e.g., square initial button)
          <div>{trigger}</div>
        ) : (
          <Button
            variant="outline"
            className="flex w-full items-center justify-between md:w-64"
          >
            <ButtonContent />
          </Button>
        )}
      </DropdownMenuTrigger>
      {isOpen && <DropdownContent />}
    </DropdownMenu>
  );
}
